import { Body1, Spinner } from '@fluentui/react-components';
import React from 'react';
import { useNavigate, useParams } from 'react-router';

import { ConfirmDeleteDialog } from '../../components/ConfirmDeleteDialog/ConfirmDeleteDialog';
import type { GraderCreationMethod } from '../../context/grader.interface';
import { GraderProvider, useGraderState } from '../../context/GraderContext';
import { useWorkspaceContext } from '../../context/WorkspaceContext';
import { useGraderDeletion } from '../../hooks/useGraderMutations/useGraderMutations';
import { useWorkspaces } from '../../hooks/useWorkspaces/useWorkspaces';

import { GraderListRail } from './GraderList/GraderListRail';
import { useStyles } from './GraderPage.style';
import {
  getCreatableGraderTypes,
  getCreateOptionByMethod,
} from './graderTypes/graderTypeRegistry.js';
import { useGraderRouteSync } from './useGraderRouteSync';
import { ViewLayout } from './ViewLayout/ViewLayout';

// Outer wrapper: gates on workspace loading, then mounts the provider.
export function GraderPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { activeWorkspace, isLoading: workspacesLoading } = useWorkspaces();
  const { activeName } = useWorkspaceContext();
  const workspaceName = activeWorkspace?.name ?? null;
  const { workspaceName: workspaceNameParam, judgeId } = useParams<{
    workspaceName: string;
    judgeId: string;
  }>();

  // When the user switches workspace via the switcher, navigate to the new
  // workspace's graders URL.
  React.useEffect(() => {
    if (activeName && workspaceNameParam && activeName !== workspaceNameParam) {
      void navigate(`/workspaces/${encodeURIComponent(activeName)}/graders`, {
        replace: true,
      });
    }
  }, [activeName, workspaceNameParam, navigate]);

  if (workspacesLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner label="Loading workspaces..." />
      </div>
    );
  }

  if (workspaceName === null) {
    return (
      <div className={styles.loadingContainer}>
        <Body1>Pick a workspace from the top bar to view its judges.</Body1>
      </div>
    );
  }

  return (
    <GraderProvider workspaceName={workspaceName} initialJudgeId={judgeId}>
      <GraderPageContent workspaceName={workspaceName} />
    </GraderProvider>
  );
}

interface GraderPageContentProps {
  workspaceName: string;
}

// Inner content: gates on judges-loading, dispatches into view/create layouts.
// All state reads from context — this component is just structural.
function GraderPageContent({ workspaceName }: GraderPageContentProps) {
  const styles = useStyles();
  const { mode, creationMethod, judgeToDelete, gradersLoading } =
    useGraderState();
  const { deleting, error, confirmDelete, cancelDelete } =
    useGraderDeletion(workspaceName);

  // Keep the selected grader and the `:judgeId` URL segment in sync (both
  // directions) without either overwriting the other. See the hook for detail.
  useGraderRouteSync(workspaceName);

  if (gradersLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner label="Loading judges..." />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <ConfirmDeleteDialog
        open={judgeToDelete !== null}
        title="Delete judge"
        deleting={deleting}
        error={error}
        onOpenChange={(open) => {
          if (!open) cancelDelete();
        }}
        onConfirm={() => void confirmDelete()}
      >
        Delete <strong>{judgeToDelete?.name}</strong>? This action cannot be
        undone.
      </ConfirmDeleteDialog>

      <div className={styles.panelsRow}>
        <GraderListRail />
        {mode === 'view' ? (
          <ViewLayout />
        ) : (
          <CreateLayout method={creationMethod} />
        )}
      </div>
    </div>
  );
}

// Routes the active creation method to its in-page layout via the grader-type
// registry. `'grader'` (Judge Factory) never reaches here — it deep-links from
// the create-method picker — so an unknown method falls back to the first
// registered in-page grader type.
function CreateLayout({ method }: { method: GraderCreationMethod }) {
  const styles = useStyles();
  const option =
    getCreateOptionByMethod(method) ?? getCreatableGraderTypes()[0];
  return (
    <React.Suspense
      fallback={
        <div className={styles.loadingContainer}>
          <Spinner label="Loading…" />
        </div>
      }
    >
      {option?.renderLayout()}
    </React.Suspense>
  );
}
